import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { AvatarTooltip } from '@/components/AvatarTooltip';

export interface AssigneeChipsProps {
  items: Array<{ id: string; name: string }>;
  selectedIds: string[];
  onToggle: (id: string) => void;
  maxVisible?: number;
}

const colorFromSeed = (seed: string) => {
  const colors = ['bg-blue-500','bg-green-500','bg-purple-500','bg-orange-500','bg-pink-500','bg-indigo-500','bg-teal-500','bg-red-500','bg-yellow-500','bg-cyan-500'];
  const sum = Array.from(seed).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return colors[sum % colors.length];
};

const toInitials = (display?: string) => {
  if (!display) return '';
  const parts = display.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

export const AssigneeChips: React.FC<AssigneeChipsProps> = ({ items, selectedIds, onToggle, maxVisible = 6 }) => {
  const visible = useMemo(() => items.slice(0, maxVisible), [items, maxVisible]);
  const remaining = useMemo(() => items.slice(maxVisible), [items, maxVisible]);

  // Overflow dropdown state
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [left, setLeft] = useState(0);
  const [top, setTop] = useState(0);
  const [width, setWidth] = useState(288);

  useEffect(() => {
    const update = () => {
      if (!isOpen) return;
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const desired = Math.min(320, Math.max(240, window.innerWidth - 24));
      setWidth(desired);
      let l = rect.left;
      const spaceRight = window.innerWidth - rect.left;
      if (spaceRight < desired) l = Math.max(12, window.innerWidth - desired - 12);
      setLeft(Math.round(l));
      setTop(Math.round(rect.bottom + 8));
    };
    const onClick = (e: MouseEvent) => {
      if (!isOpen) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) && anchorRef.current && !anchorRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    update();
    document.addEventListener('mousedown', onClick);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen]);

  return (
    <div className="flex items-center gap-2 -space-x-4">
      {visible.map((u) => {
        const isOn = selectedIds.includes(u.id);
        const color = colorFromSeed(u.id || u.name);
        return (
          <AvatarTooltip key={u.id} label={u.name}>
            <div
              onClick={() => onToggle(u.id)}
              className={`w-8 h-8 ${color} border-2 border-white rounded-full flex items-center justify-center text-white text-xs font-medium cursor-pointer ${isOn ? 'ring-1 ring-blue-500 ring-offset-1' : ''}`}
            >
              {toInitials(u.name)}
            </div>
          </AvatarTooltip>
        );
      })}
      {remaining.length > 0 && (
        <div ref={anchorRef} className="flex items-center gap-2 -space-x-4 relative">
          <span onClick={() => setIsOpen((v) => !v)} className="flex items-center justify-center w-8 h-8 text-xs text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-full cursor-pointer select-none" title="Show more members">
            +{remaining.length}
          </span>
          {isOpen && (
            <div ref={dropdownRef} className="fixed z-40 bg-white border border-gray-200 rounded-md shadow-lg" style={{ left, top, width, maxWidth: 'calc(100vw - 24px)' }}>
              <div className="p-2 border-b border-gray-200">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input className="w-full h-8 pl-10 pr-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="Search assignee" value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto p-2">
                {remaining
                  .filter((u) => (u.name || '').toLowerCase().includes(query.toLowerCase()))
                  .map((u) => {
                    const color = colorFromSeed(u.id || u.name);
                    const checked = selectedIds.includes(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-3 px-2 py-2 rounded hover:bg-gray-50 text-sm text-gray-700">
                        <input type="checkbox" className="rounded" checked={checked} onChange={() => onToggle(u.id)} />
                        <AvatarTooltip label={u.name} position="bottom">
                          <span
                            className={`w-6 h-6 ${color} rounded-full flex items-center justify-center text-white text-[10px] font-medium`}
                          >
                            {toInitials(u.name)}
                          </span>
                        </AvatarTooltip>
                        <span className="truncate">{u.name}</span>
                      </label>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssigneeChips;



